<template>
  <div
    ref="modal"
    class="modal fade"
    tabindex="-1"
    aria-hidden="true"
    aria-labelledby="operationBoardTitle"
  >
    <div class="modal-dialog modal-fullscreen">
      <div class="modal-content ob">
        <button
          type="button"
          class="btn-close ob-close"
          data-bs-dismiss="modal"
          :aria-label="$t('operationBoard.close')"
        ></button>

        <div class="modal-body ob-body">
          <div class="ob-cols">
            <!-- 1. termék + műveleti lista -->
            <div class="ob-col">
              <div class="ob-card">
                <div class="ob-prod">
                  <img
                    v-if="image"
                    :src="image"
                    :alt="serial"
                    class="ob-prod-shot"
                  />
                  <div v-else class="ob-prod-shot ob-prod-shot--empty"></div>
                  <div class="ob-prod-main">
                    <div class="ob-prod-serial">{{ serial || "–" }}</div>
                    <div class="ob-prod-name">{{ name }}</div>
                  </div>
                  <!-- app-gombok a kártya jobb felső sarkában -->
                  <div class="ob-apps">
                    <button
                      type="button"
                      class="ob-app"
                      :title="$t('operationBoard.treeTitle')"
                      :aria-label="$t('operationBoard.treeTitle')"
                      @click="openTree"
                    >
                      <span class="ob-app-emo">🌳</span>
                    </button>
                    <button
                      type="button"
                      class="ob-app"
                      :title="$t('operationBoard.print')"
                      :aria-label="$t('operationBoard.print')"
                      @click="printSheet"
                    >
                      <BaseIcon name="printer" />
                    </button>
                    <label
                      class="ob-app ob-app--lock"
                      :class="{ 'is-on': locked }"
                      :title="$t('operationBoard.lock')"
                    >
                      <input
                        type="checkbox"
                        v-model="locked"
                        @change="toggleLock"
                      />
                      <BaseIcon :name="locked ? 'lock' : 'unlock'" />
                    </label>
                  </div>
                </div>
              </div>

              <div
                class="ob-card ob-list"
                :key="listKey"
                :class="{
                  'is-locked': locked,
                  'is-dragging': dragKey !== null,
                }"
              >
                <div class="ob-search">
                  <input
                    type="text"
                    class="form-control"
                    v-model="query"
                    :placeholder="$t('operationBoard.search')"
                  />
                </div>
                <div class="ob-dirty-note" v-if="dirty && !locked">
                  {{ $t("operationBoard.savingSoon") }}
                  <button
                    type="button"
                    class="btn btn-primary btn-sm"
                    @click="save()"
                  >
                    {{ $t("operationBoard.save") }}
                  </button>
                </div>
                <div class="ob-locked-note" v-if="locked">
                  <BaseIcon name="lock" />
                  {{ $t("operationBoard.lockedHint") }}
                </div>
                <div class="ob-list-body">
                  <template v-for="(ph, pi) in phases" :key="'ph' + pi">
                    <div class="ob-sep" v-if="ph.threads.length > 1">
                      <span>⚡</span>
                      {{ pi + 1 }}. {{ $t("operationBoard.phase") }} —
                      {{ ph.threads.length }} {{ $t("operationBoard.atOnce") }}
                      <i class="ob-rule"></i>
                    </div>
                    <template v-for="th in ph.threads" :key="th.key">
                      <!-- csoport: egy szálon belül egymás utáni lépések -->
                      <div class="ob-grp" v-if="th.steps.length > 1">
                        <div class="ob-grp-head">
                          {{ $t("operationBoard.group") }} {{ pi + 1 }}
                          <span
                            >{{ th.steps.length }}
                            {{ $t("operationBoard.step") }}</span
                          >
                        </div>
                        <template v-for="(s, si) in th.steps" :key="s.key">
                          <div
                            class="ob-drop"
                            :class="{ 'is-hot': dropKey === 'b' + s.key }"
                            @dragover.prevent="dropKey = 'b' + s.key"
                            @dragleave="dropKey = null"
                            @drop.prevent="onDropBetween($event, s)"
                          ></div>
                          <div
                            class="ob-op"
                            :class="{
                              'is-sel': s.key === selectedKey,
                              'is-dim': !matches(s),
                              'is-drag': dragKey === s.key,
                              'is-over': dropKey === 'g' + s.key,
                            }"
                            :draggable="locked ? 'false' : 'true'"
                            @dragstart="onDragStart($event, s)"
                            @dragend="onDragEnd"
                            @dragover.prevent="dropKey = 'g' + s.key"
                            @dragleave="dropKey = null"
                            @drop.prevent="onDropOnRow($event, s)"
                            @click="select(s)"
                          >
                            <span class="ob-op-no"
                              >{{ pi + 1 }}/{{ si + 1 }}</span
                            >
                            <span class="ob-op-name">{{ opName(s) }}</span>
                            <span class="ob-op-right">
                              <span class="ob-acts">
                                <button
                                  type="button"
                                  class="ob-act"
                                  :title="$t('operationBoard.edit')"
                                  @click.stop="openEdit(s)"
                                >
                                  <BaseIcon name="edit" />
                                </button>
                                <button
                                  type="button"
                                  class="ob-act"
                                  :title="$t('operationBoard.addStep')"
                                  @click.stop="addStepAfter(s)"
                                >
                                  <BaseIcon name="add" />
                                </button>
                                <button
                                  type="button"
                                  class="ob-act ob-act--del"
                                  :title="$t('operationBoard.delete')"
                                  @click.stop="removeStep(s)"
                                >
                                  <BaseIcon name="delete-round" />
                                </button>
                              </span>
                              <span class="ob-chip">{{ timeLabel(s) }}</span>
                              <i
                                class="ob-dot"
                                :class="{ 'is-none': !s.videos_count }"
                              ></i>
                            </span>
                            <span class="ob-op-desc">{{
                              s.description ||
                              $t("operationBoard.noDescription")
                            }}</span>
                            <span
                              class="ob-par-zone"
                              v-if="dragKey !== null && dragKey !== s.key"
                              :class="{ 'is-hot': dropKey === 'p' + s.key }"
                              @dragover.prevent="dropKey = 'p' + s.key"
                              @dragleave="dropKey = null"
                              @drop.prevent.stop="onDropParallel($event, s)"
                              >∥</span
                            >
                          </div>
                        </template>
                      </div>
                      <!-- önálló művelet -->
                      <template v-else>
                        <div
                          class="ob-drop"
                          :class="{
                            'is-hot': dropKey === 'b' + th.steps[0].key,
                          }"
                          @dragover.prevent="dropKey = 'b' + th.steps[0].key"
                          @dragleave="dropKey = null"
                          @drop.prevent="onDropBetween($event, th.steps[0])"
                        ></div>
                        <div
                          class="ob-op ob-op--card"
                          :class="{
                            'is-sel': th.steps[0].key === selectedKey,
                            'is-dim': !matches(th.steps[0]),
                            'is-drag': dragKey === th.steps[0].key,
                            'is-over': dropKey === 'g' + th.steps[0].key,
                          }"
                          :draggable="locked ? 'false' : 'true'"
                          @dragstart="onDragStart($event, th.steps[0])"
                          @dragend="onDragEnd"
                          @dragover.prevent="dropKey = 'g' + th.steps[0].key"
                          @dragleave="dropKey = null"
                          @drop.prevent="onDropOnRow($event, th.steps[0])"
                          @click="select(th.steps[0])"
                        >
                          <span class="ob-op-no">{{ pi + 1 }}</span>
                          <span class="ob-op-name">{{
                            opName(th.steps[0])
                          }}</span>
                          <span class="ob-op-right">
                            <span class="ob-acts">
                              <button
                                type="button"
                                class="ob-act"
                                :title="$t('operationBoard.edit')"
                                @click.stop="openEdit(th.steps[0])"
                              >
                                <BaseIcon name="edit" />
                              </button>
                              <button
                                type="button"
                                class="ob-act"
                                :title="$t('operationBoard.addStep')"
                                @click.stop="addStepAfter(th.steps[0])"
                              >
                                <BaseIcon name="add" />
                              </button>
                              <button
                                type="button"
                                class="ob-act ob-act--del"
                                :title="$t('operationBoard.delete')"
                                @click.stop="removeStep(th.steps[0])"
                              >
                                <BaseIcon name="delete-round" />
                              </button>
                            </span>
                            <span class="ob-chip">{{
                              timeLabel(th.steps[0])
                            }}</span>
                            <i
                              class="ob-dot"
                              :class="{ 'is-none': !th.steps[0].videos_count }"
                            ></i>
                          </span>
                          <span class="ob-op-desc">{{
                            th.steps[0].description ||
                            $t("operationBoard.noDescription")
                          }}</span>
                          <span
                            class="ob-par-zone"
                            v-if="
                              dragKey !== null && dragKey !== th.steps[0].key
                            "
                            :class="{
                              'is-hot': dropKey === 'p' + th.steps[0].key,
                            }"
                            @dragover.prevent="dropKey = 'p' + th.steps[0].key"
                            @dragleave="dropKey = null"
                            @drop.prevent.stop="
                              onDropParallel($event, th.steps[0])
                            "
                            >∥</span
                          >
                        </div>
                      </template>
                    </template>
                  </template>
                </div>
              </div>
            </div>

            <!-- 2. szerkesztő + videó. Ha a gyártási fa nyitva van, ugyanez
                 a példány átköltözik a fa oldalsávjába — nem másolat, így az
                 épp beírt szöveg sem vész el. -->
            <Teleport :to="sidePort" :disabled="!sidePort">
              <div class="ob-col" :class="{ 'ob-col--ported': !!sidePort }">
                <div class="ob-card ob-editor" v-if="current">
                  <div class="ob-editor-head">
                    <strong>{{ opName(current) }}</strong>
                  </div>
                  <div class="ob-editor-body">
                    <div class="obf-field">
                      <label class="obf-label">{{
                        $t("operationBoard.operation")
                      }}</label>
                      <select
                        class="form-control form-control-sm"
                        :disabled="locked"
                        v-model="current.manufacturing_operation_id"
                      >
                        <option :value="null">—</option>
                        <option
                          v-for="o in operationList"
                          :key="o.id"
                          :value="o.id"
                        >
                          {{ o.name }}
                        </option>
                      </select>
                    </div>
                    <div class="obf-field">
                      <label class="obf-label">{{
                        $t("operationBoard.description")
                      }}</label>
                      <!-- a leírást nem gépeljük: a változat-ellenőrzőben
                           választunk vagy hozunk létre újat, hogy ne
                           duplázódjanak az egy szóban eltérő szövegek -->
                      <button
                        type="button"
                        class="obf-desc"
                        :disabled="locked"
                        @click="openVariants"
                      >
                        <span
                          class="obf-desc-text"
                          :class="{ 'is-empty': !current.description }"
                          >{{
                            current.description ||
                            $t("operationBoard.noDescription")
                          }}</span
                        >
                        <span class="obf-desc-count"
                          >{{ variantCount }}
                          {{ $t("operationBoard.variants") }}</span
                        >
                      </button>
                    </div>
                    <div class="obf-field">
                      <label class="obf-label">{{
                        $t("operationBoard.extraInfo")
                      }}</label>
                      <textarea
                        class="form-control form-control-sm"
                        rows="2"
                        :disabled="locked"
                        v-model="current.extra_info"
                      ></textarea>
                    </div>
                    <div class="obf-row">
                      <div class="obf-field">
                        <label class="obf-label">{{
                          $t("operationBoard.time")
                        }}</label>
                        <div class="obf-time">
                          <input
                            type="number"
                            class="form-control form-control-sm"
                            :disabled="locked"
                            v-model.number="current.time"
                          />
                          <span>mp</span>
                        </div>
                      </div>
                      <div class="obf-field">
                        <label class="obf-label">{{
                          $t("operationBoard.parallel")
                        }}</label>
                        <div class="form-check">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :disabled="locked || !currentTop"
                            v-model="parallelFlag"
                            :id="uid + 'ParallelTop'"
                          />
                          <label
                            class="form-check-label"
                            :for="uid + 'ParallelTop'"
                          >
                            {{ $t("operationBoard.parallelHint") }}
                          </label>
                        </div>
                        <p
                          class="ob-par-note"
                          v-if="currentTop?.type === 'group'"
                        >
                          {{ $t("operationBoard.parallelGroup") }}
                        </p>
                      </div>
                    </div>
                    <button
                      class="btn btn-primary btn-sm w-100"
                      :disabled="locked"
                      @click="save()"
                    >
                      {{ $t("operationBoard.save") }}
                    </button>
                  </div>
                </div>

                <div class="ob-player ob-video-card">
                  <div class="ob-player-top" v-if="current">
                    <h4>{{ opName(current) }}</h4>
                    <p>{{ current.description }}</p>
                  </div>
                  <div class="ob-player-chips" v-if="current">
                    <span class="ob-vchip">{{ timeLabel(current) }}/db</span>
                  </div>
                  <video
                    v-if="videoFile"
                    :key="videoFile.id"
                    class="ob-video"
                    :src="videoSrc(videoFile)"
                    :poster="videoPoster(videoFile)"
                    controls
                    playsinline
                    preload="metadata"
                  ></video>
                  <div class="ob-player-empty" v-else>
                    <strong>{{ $t("operationBoard.noVideoTitle") }}</strong>
                    <span>{{ $t("operationBoard.noVideoHint") }}</span>
                    <button
                      type="button"
                      class="btn btn-primary btn-sm"
                      @click="openVideos"
                    >
                      {{ $t("operationBoard.manageVideos") }}
                    </button>
                  </div>
                </div>
                <div class="ob-video-foot">
                  <button class="btn btn-secondary btn-sm" @click="openVideos">
                    {{ $t("operationBoard.manageVideos") }}
                  </button>
                </div>
              </div>
            </Teleport>

            <!-- 3. azonos művelet -->
            <div class="ob-col">
              <div class="ob-card ob-side">
                <div class="ob-side-head">
                  <div class="ob-side-top">
                    <span class="ob-side-label">{{
                      $t("operationBoard.sameOperation")
                    }}</span>
                    <button
                      type="button"
                      class="ob-variants"
                      v-if="variantCount > 1"
                      @click="openVariants"
                      :title="$t('operationBoard.variantsHint')"
                    >
                      <BaseIcon name="share" />
                      <em>{{ variantCount }}</em>
                      {{ $t("operationBoard.variants") }}
                    </button>
                  </div>
                  <div class="ob-side-op" v-if="current">
                    {{ opName(current) }}
                  </div>
                  <div class="ob-side-desc" v-if="current">
                    {{ current.description }}
                  </div>
                  <label class="ob-filter" v-if="sameAll.length">
                    <input type="checkbox" v-model="sameOnly" />
                    {{ $t("operationBoard.sameOnly") }}
                    <span class="ob-count"
                      >{{ sameList.length }}/{{ sameAll.length }}</span
                    >
                  </label>
                </div>
                <div class="ob-side-body">
                  <div v-if="sameLoading" class="ob-empty">
                    {{ $t("operationBoard.loading") }}
                  </div>
                  <div v-else-if="!sameList.length" class="ob-empty">
                    {{ $t("operationBoard.sameEmpty") }}
                  </div>
                  <div
                    v-for="(p, i) in sameList"
                    :key="'s' + i"
                    class="ob-row"
                    :class="{ 'is-self': String(p.product_id) === String(id) }"
                  >
                    <img
                      v-if="p.image"
                      :src="p.image"
                      class="ob-row-shot"
                      alt=""
                    />
                    <span v-else class="ob-row-shot ob-row-shot--empty"></span>
                    <span class="ob-row-main">
                      <span class="ob-row-serial"
                        >{{ p.serial || p.product_id }}
                        <em v-if="String(p.product_id) === String(id)">{{
                          $t("operationBoard.thisOne")
                        }}</em>
                      </span>
                      <span class="ob-row-desc">{{ p.description }}</span>
                    </span>
                    <span class="ob-chip ob-chip--time">
                      <BaseIcon name="clock" />{{ p.time }} mp/db
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 4. kategória időrangsor -->
            <div class="ob-col">
              <div class="ob-card ob-side">
                <div class="ob-side-head">
                  <span class="ob-side-label">{{
                    $t("operationBoard.categoryRank")
                  }}</span>
                  <div class="ob-side-op">{{ categoryName || "–" }}</div>
                </div>
                <div class="ob-side-body">
                  <div class="ob-empty ob-empty--todo">
                    {{ $t("operationBoard.categoryMissing") }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- felugró szerkesztő: nem bootstrap modal, hogy ne ütközzön a szülővel -->
    <div class="ob-dlg-wrap" v-if="editStep" @click.self="closeEdit">
      <div class="ob-dlg" role="dialog" aria-modal="true">
        <div class="ob-dlg-head">
          <h5>{{ $t("operationBoard.editTitle") }}</h5>
          <button
            type="button"
            class="btn-close"
            :aria-label="$t('operationBoard.close')"
            @click="closeEdit"
          ></button>
        </div>
        <div class="ob-dlg-body">
          <div class="obf-field">
            <label class="obf-label">{{
              $t("operationBoard.operation")
            }}</label>
            <select
              class="form-control form-control-sm"
              v-model="editStep.manufacturing_operation_id"
            >
              <option :value="null">—</option>
              <option v-for="o in operationList" :key="o.id" :value="o.id">
                {{ o.name }}
              </option>
            </select>
          </div>
          <div class="obf-field">
            <label class="obf-label">{{
              $t("operationBoard.description")
            }}</label>
            <input
              type="text"
              class="form-control form-control-sm"
              v-model="editStep.description"
            />
          </div>
          <div class="obf-field">
            <label class="obf-label">{{
              $t("operationBoard.extraInfo")
            }}</label>
            <textarea
              class="form-control form-control-sm"
              rows="3"
              v-model="editStep.extra_info"
            ></textarea>
          </div>
          <div class="obf-row">
            <div class="obf-field">
              <label class="obf-label">{{ $t("operationBoard.time") }}</label>
              <div class="obf-time">
                <input
                  type="number"
                  class="form-control form-control-sm"
                  v-model.number="editStep.time"
                />
                <span>mp</span>
              </div>
            </div>
            <div class="obf-field">
              <label class="obf-label">{{
                $t("operationBoard.parallel")
              }}</label>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="editStep.parallel"
                  :id="uid + 'Parallel'"
                />
                <label class="form-check-label" :for="uid + 'Parallel'">
                  {{ $t("operationBoard.parallelHint") }}
                </label>
              </div>
            </div>
          </div>
        </div>
        <div class="ob-dlg-foot">
          <button class="btn btn-secondary btn-sm" @click="closeEdit">
            {{ $t("operationBoard.cancel") }}
          </button>
          <button class="btn btn-primary btn-sm ms-auto" @click="applyEdit">
            {{ $t("operationBoard.save") }}
          </button>
        </div>
      </div>
    </div>

    <!-- változat-ellenőrző: ugyanaz a művelet, eltérő leírásokkal -->
    <div
      class="ob-dlg-wrap"
      v-if="variantsOpen"
      @click.self="variantsOpen = false"
    >
      <div class="ob-dlg ob-dlg--wide" role="dialog" aria-modal="true">
        <div class="ob-dlg-head">
          <div>
            <h5>
              {{ opName(current) }} — {{ variantCount }}
              {{ $t("operationBoard.variants") }}
            </h5>
            <p class="ob-dlg-sub">{{ $t("operationBoard.variantsHint") }}</p>
          </div>
          <div class="ob-vstats">
            <div class="ob-vstat">
              <span>{{ $t("operationBoard.products") }}</span>
              <b>{{ sameAll.length }}</b>
            </div>
            <div class="ob-vstat">
              <span>{{ $t("operationBoard.timeSpread") }}</span>
              <b>{{ timeSpread }} mp</b>
            </div>
          </div>
          <button
            type="button"
            class="btn-close"
            :aria-label="$t('operationBoard.close')"
            @click="variantsOpen = false"
          ></button>
        </div>

        <div class="ob-dlg-body ob-vgrid">
          <div class="ob-vcol">
            <div class="ob-vsearch">
              <input
                type="text"
                class="form-control form-control-sm"
                autocomplete="off"
                :placeholder="$t('operationBoard.variantSearch')"
                v-model="variantQuery"
                @input="searchLib"
              />
              <button
                v-if="canCreateDescription"
                type="button"
                class="ob-vcreate"
                @click="createDescription"
              >
                ＋ {{ $t("operationBoard.variantCreate") }}
                <em>{{ variantQuery.trim() }}</em>
              </button>
            </div>
            <div class="ob-vlist">
              <button
                v-for="v in variantList"
                :key="v.key"
                type="button"
                class="ob-variant"
                :class="{ 'is-sel': v.key === activeVariant?.key }"
                @click="selectVariant(v)"
              >
                <span class="ob-variant-text">{{ v.text }}</span>
                <span class="ob-variant-this" v-if="v.isCurrent">{{
                  $t("operationBoard.thisOne")
                }}</span>
                <span v-else></span>
                <span class="ob-chip">{{ v.timeLabel }}</span>
                <span class="ob-variant-count"
                  >{{ v.items.length }} {{ $t("operationBoard.product") }}</span
                >
              </button>
              <div class="ob-vempty" v-if="!variantList.length">
                {{ $t("operationBoard.noMatch") }}
              </div>
            </div>
          </div>

          <div class="ob-vprod">
            <div class="ob-vprod-head">
              <span class="ob-side-label">{{
                $t("operationBoard.variantProducts")
              }}</span>
              <div class="ob-side-op">{{ activeVariant?.text }}</div>
            </div>
            <div class="ob-vprod-body">
              <div class="ob-vempty" v-if="itemsLoading">
                {{ $t("operationBoard.loading") }}
              </div>
              <div class="ob-vempty" v-else-if="!shownItems.length">
                {{ $t("operationBoard.sameEmpty") }}
              </div>
              <button
                v-for="(p, i) in shownItems"
                :key="'v' + i"
                type="button"
                class="ob-row ob-row--pick"
                :class="{
                  'is-self': String(p.product_id) === String(id),
                  'is-sel': variantProduct === p,
                }"
                @click="pickVariantProduct(p)"
              >
                <img v-if="p.image" :src="p.image" class="ob-row-shot" alt="" />
                <span v-else class="ob-row-shot ob-row-shot--empty"></span>
                <span class="ob-row-main">
                  <span class="ob-row-serial">{{
                    p.serial || p.product_id
                  }}</span>
                  <span class="ob-row-desc">{{ p.name || p.description }}</span>
                </span>
                <span class="ob-chip">{{ p.time }} mp/db</span>
              </button>
            </div>
          </div>

          <!-- harmadik hasáb: a kiválasztott termék videója -->
          <div class="ob-vvideo">
            <div class="ob-vprod-head">
              <span class="ob-side-label">{{
                $t("operationBoard.video")
              }}</span>
              <div class="ob-side-op">
                {{ variantProduct?.serial || $t("operationBoard.pickProduct") }}
              </div>
            </div>
            <div class="ob-vvideo-body">
              <video
                v-if="variantVideo"
                :key="variantVideo.id"
                class="ob-video"
                :src="videoSrc(variantVideo)"
                :poster="videoPoster(variantVideo)"
                controls
                playsinline
                preload="metadata"
              ></video>
              <div class="ob-player-empty" v-else>
                <strong>{{
                  variantLoading
                    ? $t("operationBoard.loading")
                    : variantProduct
                    ? $t("operationBoard.noVideoTitle")
                    : $t("operationBoard.pickProduct")
                }}</strong>
              </div>
            </div>
          </div>
        </div>

        <div class="ob-dlg-foot ob-dlg-foot--apply">
          <span class="ob-dlg-note">{{ $t("operationBoard.spreadNote") }}</span>
          <button
            type="button"
            class="btn btn-primary btn-sm"
            :disabled="locked || !activeVariant || activeVariant.isCurrent"
            @click="applyVariant"
          >
            {{ $t("operationBoard.variantApply") }}
          </button>
        </div>
      </div>
    </div>

    <OperationPrintSheet
      v-if="printing"
      :phases="phases"
      :operations="operations"
      :serial="serial"
      :name="name"
      :image="image"
      :step-count="stepCount"
      :sum-time="sumTime"
      :real-time="realTime"
    />

    <OperationTreeModal
      ref="treeModal"
      :steps="steps"
      :operations="operations"
      :serial="serial"
      :name="name"
      :image="image"
      :editable="!locked"
      @select="onTreeSelect"
      @update="onTreeUpdate"
      @side="onTreeSide"
    />
    <ManufacturingOperationVideosModal
      ref="videosModal"
      :operation-id="current ? current.manufacturing_operation_id : null"
      :product-id="id"
    />
  </div>
</template>

<script>
const bootstrap = require("bootstrap");

/** a lista soronként példányosítja a táblát — az azonosítók így egyediek */
let boardSeq = 0;
import http from "@/modules/http";
import tenant from "@/modules/tenant";
import ManufacturingOperationVideosModal from "./ManufacturingOperationVideosModal";
import OperationTreeModal from "./OperationTreeModal";
import OperationPrintSheet from "./OperationPrintSheet";
import BaseIcon from "../icons/BaseIcon";

/** a zár értéke a listából jöhet "0"/"1" szövegként is, amit a JS igaznak vesz */
function truthy(v) {
  return v === true || v === 1 || v === "1" || v === "true";
}

/**
 * Videó Ellenőrző stílusú nézet egy termék műveleti sorára.
 * Csak megjelenít — a meglévő ManufacturingOperationsModal marad a szerkesztő.
 */
export default {
  name: "OperationBoardModal",
  components: {
    ManufacturingOperationVideosModal,
    OperationTreeModal,
    OperationPrintSheet,
    BaseIcon,
  },
  props: {
    id: Number,
    modelValue: Array,
    // a lista teljes sor-rekordja: cikkszám, név, kép
    item: Object,
    // a listából érkezik, lehet szöveg vagy szám is
    lock: [Boolean, String, Number],
  },
  emits: ["update:modelValue", "update:lock"],
  data() {
    return {
      steps: [],
      operations: {},
      operationList: [],
      query: "",
      nextKey: 1,
      selectedKey: null,
      printing: false,
      videos: [],
      variantsOpen: false,
      variantQuery: "",
      libTimer: null,
      descLib: [],
      libLoading: false,
      variantItems: [],
      itemsLoading: false,
      variantProduct: null,
      variantVideos: [],
      variantLoading: false,
      variantKey: null,
      dragKey: null,
      dropKey: null,
      locked: truthy(this.lock),
      boardOpen: false,
      dirty: false,
      pendingDrop: null,
      saveTimer: null,
      listKey: 0,
      sidePort: null,
      uid: "ob" + ++boardSeq,
      editStep: null,
      editTarget: null,
      sameAll: [],
      sameOnly: false,
      sameLoading: false,
      myModal: undefined,
    };
  },
  watch: {
    /**
     * A zárat csak megnyitáskor és a saját kapcsolóddal vesszük figyelembe.
     * Mentés után a szerver visszaküldheti zároltként, ami eddig a nézet
     * hátralévő részére letiltotta a szerkesztést.
     */
    lock(val) {
      if (this.boardOpen) return;
      this.locked = truthy(val);
    },
  },
  computed: {
    /** a felső szintű elem, amelyik a kiválasztott lépést tartalmazza */
    currentTop() {
      const c = this.current;
      if (!c) return null;
      return (
        this.steps.find(
          (it) => it === c || (it.type === "group" && it.steps.includes(c))
        ) || null
      );
    },
    /**
     * A párhuzamosság a felső szintű elemhez tartozik, nem a lépéshez:
     * egy csoport egészben fut az előzővel egyszerre.
     */
    parallelFlag: {
      get() {
        return !!this.currentTop?.parallel;
      },
      set(v) {
        if (this.currentTop) this.currentTop.parallel = v;
      },
    },
    videoServer() {
      return tenant.getAppVideoUrl ? tenant.getAppVideoUrl() : "";
    },
    /** az első lejátszható (nem Google Drive-os) felvétel */
    videoFile() {
      return this.videos.find((v) => !v.remote) || null;
    },
    mediaServer() {
      return tenant.getAppMediaUrl ? tenant.getAppMediaUrl() : "";
    },
    serial() {
      return this.item?.serial ?? this.item?.sku ?? null;
    },
    name() {
      return this.item?.name ?? "";
    },
    image() {
      const img = this.item?.image;
      if (!img) return null;
      return String(img).indexOf("http") === 0
        ? img
        : this.mediaServer + "/" + img;
    },
    categoryName() {
      return this.item?.category_name ?? null;
    },
    flatSteps() {
      const out = [];
      const walk = (list) => {
        for (const item of list || []) {
          if (item.type === "group") walk(item.steps);
          else out.push(item);
        }
      };
      walk(this.steps);
      return out;
    },
    stepCount() {
      return this.flatSteps.length;
    },
    sameList() {
      if (!this.sameOnly || !this.current) return this.sameAll;
      const want = this.norm(this.current.description);
      return this.sameAll.filter((p) => this.norm(p.description) === want);
    },
    /**
     * A művelet leírás-változatai. A könyvtárból jönnek; ahol már ismerjük a
     * termékeket (az azonos leírásúaknál), ott azt is hozzáfűzzük.
     */
    variants() {
      const own = this.current ? this.norm(this.current.description) : null;
      const map = new Map();
      this.descLib.forEach((d) => {
        const k = this.norm(d.description);
        if (map.has(k)) return;
        map.set(k, {
          key: k,
          text: d.description,
          time: Number(d.time) || 0,
          items: [],
          isCurrent: k === own,
        });
      });
      // a mostani leírás termékei már megvannak, ne kelljen újra lekérni
      this.sameAll.forEach((p) => {
        const k = this.norm(p.description);
        if (!map.has(k)) {
          map.set(k, {
            key: k,
            text: p.description,
            time: 0,
            items: [],
            isCurrent: k === own,
          });
        }
        map.get(k).items.push(p);
      });
      const out = [...map.values()];
      out.forEach((v) => {
        const times = v.items.map((x) => Number(x.time) || 0);
        if (times.length) {
          const lo = Math.min(...times);
          const hi = Math.max(...times);
          v.timeLabel = lo === hi ? lo + " mp/db" : lo + "–" + hi + " mp/db";
        } else {
          v.timeLabel = v.time ? v.time + " mp/db" : "– mp";
        }
      });
      return out.sort((a, b) =>
        a.isCurrent === b.isCurrent
          ? b.items.length - a.items.length
          : a.isCurrent
          ? -1
          : 1
      );
    },
    activeVariant() {
      return (
        this.variants.find((v) => v.key === this.variantKey) ||
        this.variants.find((v) => v.isCurrent) ||
        this.variants[0] ||
        null
      );
    },
    /** a leghosszabb és a legrövidebb normaidő különbsége */
    timeSpread() {
      const t = this.sameAll.map((p) => Number(p.time) || 0);
      if (!t.length) return 0;
      return Math.max(...t) - Math.min(...t);
    },
    variantCount() {
      return this.variants.length;
    },
    /** a találatok — a szűrést a szerver végzi, itt csak a sorrend számít */
    variantList() {
      return this.variants;
    },
    /** csak akkor ajánljuk fel az újat, ha tényleg nincs ilyen */
    canCreateDescription() {
      const q = this.variantQuery.trim();
      if (!q || this.locked) return false;
      const k = this.norm(q);
      return !this.variants.some((v) => this.norm(v.text) === k);
    },
    variantVideo() {
      return this.variantVideos[0] || null;
    },
    /** a kiválasztott változat termékei — vagy a már meglévők, vagy a lekértek */
    shownItems() {
      const v = this.activeVariant;
      if (!v) return [];
      return v.items.length ? v.items : this.variantItems;
    },
    current() {
      return this.flatSteps.find((s) => s.key === this.selectedKey) || null;
    },
    /**
     * Fázisokra bontás: új fázis ott kezdődik, ahol a `parallel` nincs
     * bekapcsolva; az utána jövő párhuzamos elemek ugyanahhoz csatlakoznak.
     * Egy `group` elem egyetlen szál, benne egymás utáni lépésekkel.
     */
    phases() {
      const result = [];
      (this.steps || []).forEach((item, i) => {
        const list = item.type === "group" ? item.steps : [item];
        const thread = {
          key: item.key,
          steps: list,
          time: list.reduce((a, s) => a + (Number(s.time) || 0), 0),
        };
        if (i === 0 || !item.parallel) result.push({ threads: [thread] });
        else result[result.length - 1].threads.push(thread);
      });
      result.forEach((p) => {
        p.longest = Math.max(...p.threads.map((t) => t.time));
      });
      return result;
    },
    sumTime() {
      return this.flatSteps.reduce((a, s) => a + (Number(s.time) || 0), 0);
    },
    realTime() {
      return this.phases.reduce((a, p) => a + p.longest, 0);
    },
  },
  methods: {
    /** szóköz, kis/nagybetű, ékezet és záró pont nélküli összehasonlítás */
    norm(t) {
      return String(t || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\.+$/, "");
    },
    opName(step) {
      if (!step) return "";
      return (
        this.operations[step.manufacturing_operation_id] || step.name || "–"
      );
    },
    timeLabel(step) {
      return step && step.time ? step.time + " mp" : "– mp";
    },
    select(step) {
      this.selectedKey = step.key;
      this.loadSame(step);
      this.loadDescLib(step);
      this.loadVideos(step);
    },
    videoSrc(v) {
      return this.videoServer + "/" + v.path + "/720p.mp4";
    },
    videoPoster(v) {
      return this.videoServer + "/" + v.path + "/frame1.jpg";
    },
    loadVideos(step) {
      this.videos = [];
      const op = step && step.manufacturing_operation_id;
      if (!op) return;
      http
        .fetch("/files/manufacturing-operation-videos?operation=" + op)
        .then((data) => {
          this.videos = Array.isArray(data) ? data : [];
        })
        .catch(() => {
          this.videos = [];
        });
    },
    /** egy hívás a leírás-idő végpontra, képútvonalakkal kiegészítve */
    fetchTimes(op, q) {
      return http
        .fetch(
          `/manufacturing/operations/descriptions/times?q=${encodeURIComponent(
            q || ""
          )}&id=${op}&category_filter_by_product_id=${this.id}`
        )
        .then((data) =>
          (data || []).map((p) => ({
            ...p,
            image:
              p.image && String(p.image).indexOf("http") !== 0
                ? this.mediaServer + "/" + p.image
                : p.image,
          }))
        )
        .catch(() => []);
    },
    /**
     * Az azonos leírású termékek. A leírás-idő végpont SZÖVEGRE keres, üres
     * keresésre hibalapot ad — ezért mindig a lépés leírásával hívjuk.
     */
    loadSame(step) {
      this.sameAll = [];
      const op = step && step.manufacturing_operation_id;
      if (!op || !step.description) return;
      this.sameLoading = true;
      this.fetchTimes(op, step.description)
        .then((rows) => {
          if (this.current === step) this.sameAll = rows;
        })
        .finally(() => {
          this.sameLoading = false;
        });
    },
    /**
     * A művelethez tartozó ÖSSZES leírás — ez adja a változatok számát és a
     * felugró ablak listáját. Külön végpont, mert a leírás-idő keresés csak
     * egy megadott szövegre tud válaszolni.
     */
    /** gépelésre újrakérdezzük a könyvtárat — a végpont szövegre keres */
    searchLib() {
      clearTimeout(this.libTimer);
      this.libTimer = setTimeout(() => {
        this.loadDescLib(this.current, this.variantQuery);
      }, 250);
    },
    loadDescLib(step, q) {
      const op = step && step.manufacturing_operation_id;
      if (!op) {
        this.descLib = [];
        return;
      }
      this.libLoading = true;
      http
        .fetch(
          `/manufacturing/operations/descriptions/search?q=${encodeURIComponent(
            q || ""
          )}&id=${op}&category_filter_by_product_id=${this.id}`
        )
        .then((data) => {
          const seen = new Set();
          const list = (Array.isArray(data) ? data : []).filter((d) => {
            const k = this.norm(d.description);
            if (!d.description || seen.has(k)) return false;
            seen.add(k);
            return true;
          });
          if (this.current === step) this.descLib = list;
        })
        .catch(() => {
          this.descLib = [];
        })
        .finally(() => {
          this.libLoading = false;
        });
    },
    /** egy változat termékei — csak akkor kérjük le, ha rákattintanak */
    loadVariantItems(text) {
      this.variantItems = [];
      const op = this.current && this.current.manufacturing_operation_id;
      if (!op || !text) return;
      this.itemsLoading = true;
      this.fetchTimes(op, text)
        .then((rows) => {
          this.variantItems = rows;
        })
        .finally(() => {
          this.itemsLoading = false;
        });
    },
    matches(step) {
      const q = this.query.trim().toLowerCase();
      if (!q) return true;
      return (
        this.opName(step).toLowerCase().includes(q) ||
        String(step.description || "")
          .toLowerCase()
          .includes(q)
      );
    },
    /** A lépést tartalmazó tömb és index — csoportban és felső szinten is */
    locate(step) {
      const scan = (list) => {
        for (let i = 0; i < list.length; i++) {
          const item = list[i];
          if (item === step) return { list, i };
          if (item.type === "group") {
            const hit = scan(item.steps);
            if (hit) return hit;
          }
        }
        return null;
      };
      return scan(this.steps);
    },
    removeStep(step) {
      const at = this.locate(step);
      if (!at) return;
      at.list.splice(at.i, 1);
      this.selectedKey = null;
      const first = this.flatSteps[0];
      if (first) this.select(first);
    },
    addStepAfter(step) {
      const at = this.locate(step);
      if (!at) return;
      const fresh = {
        key: this.nextKey++,
        type: "step",
        step_id: null,
        manufacturing_operation_id: null,
        parallel: false,
        time: 0,
        description: "",
        extra_info: "",
        videos_count: 0,
      };
      at.list.splice(at.i + 1, 0, fresh);
      this.select(fresh);
    },
    /**
     * Ugyanazt a formát adja vissza, mint a meglévő szerkesztő mentése,
     * és ugyanúgy az `update:modelValue` eseményen keresztül menti.
     */
    save() {
      const process = (list) => {
        const out = [];
        for (const item of list) {
          if (item.type === "group") {
            const steps = process(item.steps);
            if (steps.length) {
              out.push({
                type: "group",
                parallel: item.parallel ?? false,
                steps,
                ...(item.step_id ? { step_id: item.step_id } : {}),
              });
            }
          } else if (item.manufacturing_operation_id) {
            out.push({
              type: "step",
              manufacturing_operation_id: item.manufacturing_operation_id,
              name: this.operations[item.manufacturing_operation_id],
              parallel: item.parallel ?? false,
              time: item.time ?? 0,
              description: item.description ?? "",
              extra_info: item.extra_info ?? "",
              ...(item.step_id ? { step_id: item.step_id } : {}),
            });
          }
        }
        return out;
      };
      this.$emit("update:lock", this.locked);
      this.$emit("update:modelValue", process(this.steps));
      this.dirty = false;
      // a mentés a szülő listát is frissíti; a sorokat egy lépésben, tisztán
      // építjük újra, hogy ne maradjon félig lecserélt DOM a húzás alatt
      this.listKey++;
    },
    /** fel/le nyíllal lépkedés a műveletek közt */
    onKey(e) {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      const tag = (e.target.tagName || "").toLowerCase();
      if (["input", "textarea", "select"].includes(tag)) return;
      const list = this.flatSteps;
      if (!list.length) return;
      e.preventDefault();
      const i = list.findIndex((s) => s.key === this.selectedKey);
      const next = e.key === "ArrowDown" ? i + 1 : i - 1;
      if (next < 0 || next >= list.length) return;
      this.select(list[next]);
      this.$nextTick(() => {
        const el = this.$el.querySelector(".ob-op.is-sel");
        if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
      });
    },
    onDragStart(e, step) {
      if (this.locked) {
        e.preventDefault();
        return;
      }
      this.dragKey = step.key;
      this.pendingDrop = null;
      clearTimeout(this.saveTimer);
      if (e.dataTransfer) {
        e.dataTransfer.setData("text/plain", String(step.key));
        e.dataTransfer.effectAllowed = "move";
      }
    },

    /**
     * A `drop` NEM nyúl a steps-hez. A böngésző a `dragend`-et külön taskban
     * kézbesíti, arra a DOM-elemre, amelyről a húzás indult — ha a drop
     * kezelője azonnal mutál, a Vue flush-e még a dragend előtt lecseréli
     * azt az elemet, a natív drag munkamenet nem zárul le rendesen, és a
     * következő dragstart már el sem indul. Ezért csak a szándékot
     * jegyezzük fel, a végrehajtás a dragend-ben történik.
     */
    readDragKey(e) {
      const n = Number(e?.dataTransfer?.getData("text/plain"));
      return Number.isFinite(n) ? n : this.dragKey;
    },
    onDropBetween(e, target) {
      const dragKey = this.readDragKey(e);
      this.dropKey = null;
      if (dragKey == null || this.locked) return;
      this.pendingDrop = { kind: "reorder", dragKey, targetKey: target.key };
    },
    onDropOnRow(e, target) {
      const dragKey = this.readDragKey(e);
      this.dropKey = null;
      if (dragKey == null || dragKey === target.key || this.locked) return;
      this.pendingDrop = { kind: "group", dragKey, targetKey: target.key };
    },
    onDropParallel(e, target) {
      const dragKey = this.readDragKey(e);
      this.dropKey = null;
      if (dragKey == null || dragKey === target.key || this.locked) return;
      this.pendingDrop = { kind: "parallel", dragKey, targetKey: target.key };
    },

    /**
     * Húzás után magától ment, de késleltetve: több gyors mozdulatból egy
     * mentés lesz, és soha nem esik bele egy éppen induló húzásba.
     */
    saveSoon() {
      clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => {
        if (!this.locked && this.dirty) this.save();
      }, 1200);
    },
    /** mindig lefut: sikeres ejtésnél, Esc-nél és érvénytelen célnál is */
    onDragEnd() {
      const pending = this.pendingDrop;
      this.dragKey = null;
      this.dropKey = null;
      this.pendingDrop = null;
      if (pending) this.applyPendingDrop(pending);
    },

    /**
     * A fa a saját munkamodelljében rendez át, és kész sorrendet küld vissza.
     * A lépés-objektumok ugyanazok maradnak, csak a tető-szint épül újra.
     */
    onTreeUpdate(list) {
      if (this.locked) return;
      this.steps = list;
      this.dirty = true;
      this.listKey++;
      this.saveSoon();
    },

    applyPendingDrop({ kind, dragKey, targetKey }) {
      const src = this.findStepByKey(dragKey);
      const target = this.findStepByKey(targetKey);
      if (!src || !target || src === target) return;
      if (kind === "reorder") this.reorderSteps(src, target);
      else if (kind === "group") this.groupWith(src, target);
      else if (kind === "parallel") this.makeParallel(src, target);
      else if (kind === "phase") this.makeOwnPhase(src, target);
      this.cleanupEmptyGroups();
      this.select(src);
      this.dirty = true;
      this.saveSoon();
    },

    findStepByKey(key) {
      return this.flatSteps.find((s) => s.key === key) || null;
    },
    /** a felső szintű elem, amelyik a lépést tartalmazza */
    topIndexOf(step) {
      return this.steps.findIndex(
        (it) => it === step || (it.type === "group" && it.steps.includes(step))
      );
    },
    pluck(step) {
      const at = this.locate(step);
      if (!at) return null;
      return at.list.splice(at.i, 1)[0];
    },

    /** átrendezés: a húzott elem a cél elé kerül, ugyanabban a szintben */
    reorderSteps(src, target) {
      const removed = this.pluck(src);
      if (!removed) return;
      const at = this.locate(target);
      if (at) at.list.splice(at.i, 0, removed);
      else this.steps.push(removed);
    },

    /** két művelet egymásra húzva csoportot alkot */
    groupWith(src, target) {
      if (this.topIndexOf(target) < 0) return;
      const removed = this.pluck(src);
      if (!removed) return;
      const at = this.topIndexOf(target);
      const host = this.steps[at];
      if (host && host.type === "group") {
        host.steps.push(removed);
      } else {
        this.steps.splice(at, 1, {
          key: this.nextKey++,
          type: "group",
          parallel: target.parallel ?? false,
          steps: [target, removed],
        });
      }
    },

    /** a cél MELLÉ: külön szálon, ugyanabban a fázisban fut */
    makeParallel(src, target) {
      const removed = this.pluck(src);
      if (!removed) return;
      const at = this.topIndexOf(target);
      removed.parallel = true;
      if (at < 0) this.steps.push(removed);
      else this.steps.splice(at + 1, 0, removed);
    },

    /** a cél fázisa ELÉ: saját, önálló fázisban fut */
    makeOwnPhase(src, target) {
      const removed = this.pluck(src);
      if (!removed) return;
      const at = this.topIndexOf(target);
      removed.parallel = false;
      if (at < 0) this.steps.push(removed);
      else this.steps.splice(at, 0, removed);
    },

    /** üres és egyelemű csoportok feloldása */
    cleanupEmptyGroups() {
      for (let i = this.steps.length - 1; i >= 0; i--) {
        const it = this.steps[i];
        if (it.type !== "group") continue;
        if (!it.steps.length) {
          this.steps.splice(i, 1);
        } else if (it.steps.length === 1) {
          const only = it.steps[0];
          only.parallel = it.parallel ?? false;
          this.steps.splice(i, 1, only);
        }
      }
    },

    toggleLock() {
      this.$emit("update:lock", this.locked);
    },
    printSheet() {
      this.printing = true;
      this.$nextTick(() => {
        const done = () => {
          this.printing = false;
          window.removeEventListener("afterprint", done);
        };
        window.addEventListener("afterprint", done);
        setTimeout(() => window.print(), 60);
      });
    },
    openTree() {
      if (this.$refs.treeModal?.showModal) this.$refs.treeModal.showModal();
    },
    onTreeSelect(step) {
      this.select(step);
    },
    /**
     * A fa oldalsávja megnyílt vagy bezárult; a fa magát az elemet küldi.
     * Selector nem jó: a Vue csak beillesztéskor oldaná fel, ráadásul a
     * listában soronként van egy fa, így az azonosító sem egyedi.
     */
    onTreeSide(el) {
      this.sidePort = el || null;
    },
    /** másolaton szerkesztünk, hogy a Mégse tényleg visszavonjon */
    openEdit(step) {
      this.select(step);
      this.$nextTick(() => {
        const el = this.$el.querySelector(".ob-editor input");
        if (el) el.focus();
      });
    },
    closeEdit() {
      this.editStep = null;
      this.editTarget = null;
    },
    applyEdit() {
      if (this.editTarget && this.editStep) {
        Object.assign(this.editTarget, this.editStep);
      }
      this.closeEdit();
      this.save();
      this.loadSame(this.current);
    },
    openVariants() {
      const own = this.variants.find((v) => v.isCurrent);
      this.variantKey = own ? own.key : this.variants[0]?.key || null;
      this.variantQuery = "";
      this.variantProduct = null;
      this.variantVideos = [];
      this.variantItems = [];
      this.variantsOpen = true;
    },
    selectVariant(v) {
      this.variantKey = v.key;
      this.variantProduct = null;
      this.variantVideos = [];
      this.variantItems = [];
      if (!v.items.length) this.loadVariantItems(v.text);
    },
    /** termékre kattintva jobbra betöltjük annak a videóit */
    pickVariantProduct(p) {
      this.variantProduct = p;
      this.variantVideos = [];
      const pid = p && p.product_id;
      if (!pid) return;
      this.variantLoading = true;
      http
        .fetch("/files/product-videos?product=" + pid)
        .then((data) => {
          this.variantVideos = data?.images?.[0]?.images || [];
        })
        .catch(() => {
          this.variantVideos = [];
        })
        .then(() => {
          this.variantLoading = false;
        });
    },
    /** a kiválasztott változat leírását és idejét vesszük át */
    applyVariant() {
      const v = this.activeVariant;
      if (!v || !this.current || this.locked) return;
      this.current.description = v.text;
      const t = Number(v.items[0]?.time) || 0;
      if (t) this.current.time = t;
      this.variantsOpen = false;
      this.loadSame(this.current);
    },
    /** nincs ilyen leírás: a beírt szöveggel hozunk létre újat */
    createDescription() {
      const q = this.variantQuery.trim();
      if (!q || !this.current || this.locked) return;
      this.current.description = q;
      this.variantsOpen = false;
      this.loadSame(this.current);
    },
    openVideos() {
      if (this.$refs.videosModal?.showModal) this.$refs.videosModal.showModal();
    },
    applyOperations(list) {
      const arr = Array.isArray(list) ? list : [];
      const map = {};
      arr.forEach((o) => (map[o.id] = o.name));
      this.operations = map;
      this.operationList = arr
        .slice()
        .sort((a, b) => String(a.name).localeCompare(String(b.name), "hu"));
    },
    /**
     * A műveletek a Products oldal betöltésekor bekerülnek a store-ba
     * (`setOperations`), ezért onnan vesszük. Ha valamiért üres, lekérjük —
     * a végpont `{ data: [...] }` alakban válaszol.
     */
    loadOperations() {
      const fromStore = this.$store?.state?.operations;
      if (fromStore?.length) {
        this.applyOperations(fromStore);
        return Promise.resolve();
      }
      if (this.operationList.length) return Promise.resolve();
      return http
        .fetch("/manufacturing/operations/all", null, false, null, null, false)
        .then((data) => {
          this.applyOperations(data?.data ?? data?.operations ?? data);
        })
        .catch(() => {});
    },
    showModal() {
      const map = (list) =>
        (list || []).map((val) => {
          if (val.type === "group") {
            return {
              key: this.nextKey++,
              type: "group",
              parallel: val.parallel,
              step_id: val.step_id ?? val.id ?? null,
              steps: map(val.steps),
            };
          }
          return {
            key: this.nextKey++,
            type: "step",
            // ugyanaz a képzés, mint a meglévő szerkesztőben — mentésnél
            // nem szabad eltérni a bevált úttól
            step_id: val.step_id ?? val.id ?? null,
            name: val.name ?? null,
            manufacturing_operation_id:
              val.manufacturing_operation_id ?? val.id ?? null,
            parallel: val.parallel ?? val.pivot?.parallel ?? false,
            time: val.time ?? val.pivot?.time ?? 0,
            description: val.description ?? val.pivot?.description ?? "",
            extra_info: val.extra_info ?? val.pivot?.extra_info ?? "",
            videos_count: val.videos_count ?? val.pivot?.videos_count ?? 0,
          };
        });

      this.nextKey = 1;
      this.query = "";
      this.locked = truthy(this.lock);
      this.boardOpen = true;
      this.dirty = false;
      this.steps = map(this.modelValue);
      this.loadOperations().then(() => {
        const first = this.flatSteps[0];
        if (first) this.select(first);
      });

      if (this.myModal === undefined) {
        this.myModal = new bootstrap.Modal(this.$refs.modal, {
          backdrop: "static",
        });
        this.$refs.modal.addEventListener("hidden.bs.modal", () => {
          document.removeEventListener("keydown", this.onKey);
          this.boardOpen = false;
          clearTimeout(this.saveTimer);
          if (this.dirty && !this.locked) this.save();
        });
      }
      document.addEventListener("keydown", this.onKey);
      this.myModal.show();
    },
  },
};
</script>

<style scoped>
.ob {
  position: relative;
  border: 0;
  background: linear-gradient(160deg, #eef1fb 0%, #e9edf9 45%, #f3eef7 100%);
}
.ob-close {
  position: absolute;
  top: 14px;
  right: 16px;
  z-index: 5;
  width: 34px;
  height: 34px;
  margin: 0;
  padding: 0;
  border-radius: 50%;
  background-color: #fff;
  background-size: 12px;
  border: 1px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 1px 2px rgba(28, 33, 60, 0.06),
    0 8px 20px -12px rgba(28, 33, 60, 0.5);
  opacity: 1;
}
.ob-close:hover {
  background-color: #f3f4f8;
}
.ob-head {
  align-items: baseline;
  gap: 10px;
}
.ob-sub {
  font-family: var(--mono-font, monospace);
  font-size: 12.5px;
  color: var(--gray);
}
.ob-body {
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}
.ob-cols {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) 372px minmax(0, 1fr) minmax(0, 1fr);
  gap: 14px;
  align-items: start;
}
.ob-col {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}
/* a hasáb utolsó kártyája tölti ki a maradék helyet */
.ob-col > .ob-card:last-child {
  flex: 1;
  min-height: 0;
}
/* a fa oldalsávjában a szerkesztő a rendelkezésre álló helyet tölti ki */
.ob-col--ported {
  height: auto;
  overflow: visible;
}
.ob-col > .ob-editor:last-child {
  flex: none;
}
.ob-card {
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 14px;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(28, 33, 60, 0.05),
    0 10px 26px -18px rgba(28, 33, 60, 0.45);
}

/* termékkártya */
.ob-prod {
  display: flex;
  gap: 12px;
  padding: 13px;
}
.ob-prod-shot {
  width: 56px;
  height: 72px;
  flex: none;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid var(--divider);
}
.ob-prod-shot--empty {
  background: var(--extra-light);
}
.ob-prod-main {
  min-width: 0;
}
.ob-prod-serial {
  font-size: 20px;
  font-weight: 700;
  color: #713ae8;
}
.ob-prod-name {
  font-size: 12px;
  color: var(--gray);
}
/* app-gombok: kis csempék a termékkártya jobb felső sarkában */
.ob-apps {
  display: flex;
  align-self: flex-start;
  gap: 6px;
  margin-left: auto;
}
.ob-app {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  flex: none;
  padding: 0;
  border: 1px solid var(--divider);
  border-radius: 10px;
  background: var(--white-color);
  color: var(--gray);
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(28, 33, 60, 0.06);
  transition: border-color 0.15s, color 0.15s, box-shadow 0.15s;
}
.ob-app:hover {
  border-color: #a8cbfb;
  color: #713ae8;
  box-shadow: 0 3px 8px -4px rgba(28, 33, 60, 0.35);
}
.ob-app :deep(svg) {
  width: 16px;
  height: 16px;
}
.ob-app-emo {
  font-size: 16px;
  line-height: 1;
}
.ob-app--lock input {
  display: none;
}
.ob-app--lock.is-on {
  border-color: #f0b7b2;
  background: #fdeceb;
  color: #b3261e;
}
.ob-side-top {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 2px;
}
.ob-variants {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-left: auto;
  padding: 3px 11px 3px 8px;
  border: 1px solid var(--divider);
  border-radius: 20px;
  background: #fff;
  color: #713ae8;
  font-size: 12px;
  font-weight: 700;
  box-shadow: 0 1px 2px rgba(28, 33, 60, 0.06);
}
.ob-variants:hover {
  border-color: #a8cbfb;
}
.ob-variants em {
  display: grid;
  place-items: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 10px;
  background: #713ae8;
  color: #fff;
  font-style: normal;
  font-size: 11.5px;
}
.ob-variants :deep(svg) {
  width: 13px;
  height: 13px;
  color: var(--gray);
}
.ob-filter {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 9px 0 0;
  font-size: 12px;
  font-weight: 600;
  color: var(--black);
  cursor: pointer;
}
.ob-count {
  margin-left: auto;
  padding: 1px 9px;
  border-radius: 20px;
  background: var(--extra-light);
  font-size: 11px;
  font-weight: 600;
  color: var(--gray);
}
/* műveleti lista */
.ob-list {
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.ob-search {
  padding: 10px 11px;
  border-bottom: 1px solid var(--divider);
}
.ob-list-body {
  flex: 1;
  overflow-y: auto;
  padding: 10px 11px 12px;
}
.ob-sep {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 13px 0 8px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #c2761b;
}
.ob-sep:first-child {
  margin-top: 0;
}
.ob-rule {
  flex: 1;
  height: 1px;
  background: #c2761b;
  opacity: 0.4;
}
.ob-op {
  cursor: pointer;
  display: grid;
  grid-template-columns: 32px 1fr auto;
  gap: 3px 9px;
  align-items: start;
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  margin-bottom: 6px;
  border: 1px solid transparent;
  border-radius: 9px;
  background: var(--white-color);
}
.ob-op--card {
  border-color: var(--divider);
}
.ob-op:hover {
  border-color: #a8cbfb;
}
.ob-op {
  cursor: grab;
  position: relative;
}
/* húzás közben a sor jobb szélén: ide ejtve párhuzamos szál lesz */
.ob-par-zone {
  position: absolute;
  top: 4px;
  bottom: 4px;
  right: 4px;
  width: 46px;
  display: grid;
  place-items: center;
  border: 1px dashed #a8cbfb;
  border-radius: 9px;
  background: #f7fafd;
  color: #713ae8;
  font-size: 15px;
  font-weight: 700;
}
.ob-par-zone.is-hot {
  border-style: solid;
  border-color: #713ae8;
  background: #eaf3fe;
}
.ob-list.is-locked .ob-op {
  cursor: default;
}
.ob-op:active {
  cursor: grabbing;
}
.ob-op.is-drag {
  opacity: 0.4;
}
/* egymásra húzva csoport lesz belőlük */
.ob-op.is-over {
  border-color: #713ae8;
  box-shadow: inset 0 0 0 2px #a8cbfb;
}
.ob-drop {
  height: 6px;
  margin: -3px 0;
  border-radius: 3px;
}
/* húzás közben a sorok szétnyílnak, így könnyű közéjük ejteni */
.ob-list.is-dragging .ob-drop {
  height: 30px;
  margin: 3px 0;
  border: 1px dashed #a8cbfb;
  background: #f7fafd;
}
.ob-list.is-dragging .ob-drop.is-hot {
  border-style: solid;
  border-color: #713ae8;
  background: #eaf3fe;
}
.ob-drop.is-hot {
  background: #713ae8;
}
.ob-op.is-sel {
  border-color: #713ae8;
  background: #eaf3fe;
}
.ob-op.is-dim {
  opacity: 0.32;
}
.ob-op-no {
  grid-row: span 2;
  font-size: 13px;
  font-weight: 700;
  text-align: center;
  padding: 5px 0;
  border-radius: 7px;
  background: var(--extra-light);
  color: var(--gray);
}
.ob-op.is-sel .ob-op-no {
  background: #713ae8;
  color: #fff;
}
.ob-op-name {
  font-size: 13px;
  font-weight: 600;
}
.ob-op-desc {
  grid-column: 2;
  font-size: 11.5px;
  color: var(--gray);
}
.ob-op-right {
  grid-row: span 2;
  display: flex;
  align-items: center;
  gap: 7px;
}
.ob-chip--time {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.ob-chip--time :deep(svg) {
  width: 12px;
  height: 12px;
}
.ob-chip {
  font-size: 12.5px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 7px;
  background: var(--extra-light);
  color: var(--gray);
  white-space: nowrap;
}
.ob-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #1a7f52;
}
.ob-dot.is-none {
  background: transparent;
  border: 1.5px solid var(--divider);
}
.ob-grp {
  border: 1px solid var(--divider);
  border-radius: 10px;
  background: var(--extra-light);
  padding: 7px;
  margin-bottom: 7px;
}
.ob-grp-head {
  display: flex;
  gap: 7px;
  padding: 1px 4px 7px;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--gray);
}
.ob-grp-head span {
  margin-left: auto;
}

/* videó */
.ob-par-note {
  margin: 5px 0 0;
  font-size: 11px;
  color: var(--gray);
}
.ob-dirty-note {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 12px;
  border-bottom: 1px solid var(--divider);
  background: #fdf1e0;
  color: #8a5510;
  font-size: 11.5px;
  font-weight: 600;
}
.ob-dirty-note .btn {
  margin-left: auto;
}
.ob-locked-note {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--divider);
  background: #fbe6e4;
  color: #b3261e;
  font-size: 11.5px;
  font-weight: 600;
}
.ob-locked-note :deep(svg) {
  width: 13px;
  height: 13px;
}
/* leírás: nem beírható, a változat-ellenőrzőt nyitja */
.obf-desc {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 8px 6px 10px;
  border: 1px solid var(--divider);
  border-radius: 8px;
  background: var(--white-color);
  text-align: left;
}
.obf-desc:hover:not(:disabled) {
  border-color: #a8cbfb;
}
.obf-desc:disabled {
  background: var(--extra-light);
}
.obf-desc-text {
  flex: 1;
  min-width: 0;
  font-size: 12.5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.obf-desc-text.is-empty {
  color: var(--gray);
  font-style: italic;
}
.obf-desc-count {
  flex: none;
  padding: 2px 9px;
  border-radius: 20px;
  background: #eaf3fe;
  color: #713ae8;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

/* változat-ellenőrző: kereső, létrehozás, videó hasáb */
.ob-vcol {
  display: flex;
  flex-direction: column;
  min-height: 0;
  gap: 8px;
}
.ob-vsearch {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ob-vcreate {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 10px;
  border: 1px dashed #a8cbfb;
  border-radius: 8px;
  background: #eaf3fe;
  color: #713ae8;
  font-size: 12px;
  font-weight: 700;
  text-align: left;
}
.ob-vcreate em {
  font-style: normal;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ob-vcreate:hover {
  background: #dcebfd;
}
.ob-vempty {
  padding: 14px;
  text-align: center;
  font-size: 12px;
  color: var(--gray);
}
.ob-row--pick {
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
}
.ob-row--pick:hover {
  background: var(--extra-light);
}
.ob-row--pick.is-sel {
  background: #eaf3fe;
  box-shadow: inset 2px 0 0 #713ae8;
}
.ob-vvideo {
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-left: 1px solid var(--divider);
  padding-left: 14px;
}
.ob-vvideo-body {
  flex: 1;
  min-height: 0;
  display: grid;
  place-items: center;
  padding-top: 10px;
}
.ob-vvideo-body .ob-video {
  width: 100%;
  max-height: 100%;
  border-radius: 10px;
  background: #000;
}
.ob-dlg-foot--apply {
  display: flex;
  align-items: center;
  gap: 14px;
}
.ob-dlg-note {
  flex: 1;
  font-size: 11.5px;
  color: var(--gray);
}
.ob-editor {
  flex: 0 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.ob-editor-body {
  overflow-y: auto;
  min-height: 0;
}
.ob-editor-head {
  padding: 9px 13px;
  border-bottom: 1px solid var(--divider);
  background: var(--extra-light);
  font-size: 13px;
}
.ob-editor-body {
  padding: 11px 13px 13px;
}
.ob-editor-head {
  flex: none;
}
/* a videó ne legyen ilyen magas: rögzített magasság, álló arány */
.ob-video-card {
  flex: 1;
  min-height: 180px;
  width: 100%;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(28, 33, 60, 0.08),
    0 14px 30px -18px rgba(12, 14, 28, 0.6);
}
.ob-player {
  position: relative;
  overflow: hidden;
  background: radial-gradient(
    80% 60% at 26% 10%,
    #57515f,
    #2d2936 55%,
    #1a1821
  );
}
.ob-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
}
.ob-player-top {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  padding: 12px 14px;
  background: linear-gradient(rgba(12, 10, 18, 0.82), transparent);
  color: #fff;
  z-index: 2;
}
.ob-player-top h4 {
  font-size: 15px;
  font-weight: 700;
  margin: 0;
}
.ob-player-top p {
  margin: 2px 0 0;
  font-size: 11.5px;
  opacity: 0.85;
}
.ob-player-chips {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 3;
}
.ob-vchip {
  display: inline-block;
  padding: 3px 9px;
  border-radius: 20px;
  background: rgba(12, 10, 18, 0.62);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
}
.ob-play {
  position: absolute;
  inset: 0;
  margin: auto;
  width: 62px;
  height: 62px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.85);
  background: rgba(20, 18, 26, 0.42);
  color: #fff;
  font-size: 18px;
  padding-left: 4px;
}
.ob-player-empty {
  position: absolute;
  inset: 0;
  display: grid;
  place-content: center;
  justify-items: center;
  gap: 8px;
  background: var(--extra-light);
  color: var(--gray);
  text-align: center;
  padding: 22px;
  font-size: 12px;
}
.ob-player-empty strong {
  color: var(--black);
}
.ob-video-foot {
  display: flex;
  gap: 7px;
  flex: none;
  justify-content: center;
}

/* oldalsó listák */
.ob-side-head {
  padding: 11px 13px;
  border-bottom: 1px solid var(--divider);
  background: var(--extra-light);
}
.ob-side-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--gray-light);
}
.ob-side-op {
  font-size: 14px;
  font-weight: 700;
  color: #713ae8;
}
.ob-side-desc {
  font-size: 11.5px;
  color: var(--gray);
}
.ob-side {
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.ob-side-body {
  flex: 1;
  padding: 6px 13px 12px;
  overflow-y: auto;
}
.ob-row {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) auto;
  gap: 11px;
  align-items: center;
  padding: 8px 8px;
  border-radius: 12px;
  border: 1px solid transparent;
}
/* a saját termék kiemelve, mint a referenciában: fehér lap, kék idő */
.ob-row.is-self {
  background: #fff;
  border-color: var(--divider);
  box-shadow: 0 1px 2px rgba(28, 33, 60, 0.05),
    0 8px 18px -14px rgba(28, 33, 60, 0.4);
}
.ob-row.is-self .ob-chip {
  background: transparent;
  color: #713ae8;
}
.ob-row-shot {
  width: 42px;
  height: 50px;
  border-radius: 9px;
  object-fit: cover;
  background: var(--extra-light);
  border: 1px solid var(--divider);
}
.ob-row-shot--empty {
  background: var(--extra-light);
  display: block;
}
.ob-row-main {
  min-width: 0;
}
.ob-row-serial {
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: #713ae8;
}
.ob-row-serial em {
  font-style: normal;
  color: var(--gray);
  font-weight: 400;
}
.ob-row-desc {
  display: block;
  font-size: 11.5px;
  color: var(--gray);
  line-height: 1.3;
}
.ob-empty {
  padding: 14px 6px;
  font-size: 12px;
  color: var(--gray);
}
.ob-empty--todo {
  border: 1px dashed var(--divider);
  border-radius: 8px;
  text-align: center;
}

/* soronkénti ikonok az idő előtt */
.ob-acts {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  opacity: 0;
}
.ob-op:hover .ob-acts,
.ob-op.is-sel .ob-acts {
  opacity: 1;
}
.ob-act {
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--gray);
}
.ob-act:hover {
  background: var(--extra-light);
  color: #713ae8;
}
.ob-act--del:hover {
  color: #b3261e;
}
.ob-act :deep(svg) {
  width: 15px;
  height: 15px;
}

/* felugró szerkesztő */
.ob-dlg-wrap {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(28, 27, 34, 0.45);
}
/* ugyanakkora, mint a gyártási fa ablaka */
.ob-dlg--wide {
  max-width: 1180px;
}
.ob-dlg--wide .ob-dlg-body {
  max-height: calc(100vh - 230px);
}
.ob-dlg-sub {
  margin: 3px 0 0;
  font-size: 12px;
  color: var(--gray);
  max-width: 70ch;
}
.ob-vstats {
  margin-left: auto;
  display: flex;
  gap: 9px;
}
.ob-vstat {
  min-width: 96px;
  padding: 6px 11px;
  border: 1px solid var(--divider);
  border-radius: 9px;
  text-align: center;
}
.ob-vstat span {
  display: block;
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--gray-light);
}
.ob-vstat b {
  font-size: 15px;
}
.ob-vgrid {
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr) 300px;
  gap: 14px;
}
@media (max-width: 1400px) {
  .ob-vgrid {
    grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr) 240px;
  }
}
.ob-vlist {
  display: grid;
  gap: 8px;
  align-content: start;
}
.ob-variant {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 3px 10px;
  align-items: center;
  text-align: left;
  padding: 10px 12px;
  border: 1px solid var(--divider);
  border-radius: 10px;
  background: #fff;
}
.ob-variant:hover {
  border-color: #a8cbfb;
}
.ob-variant.is-sel {
  border-color: #713ae8;
  box-shadow: inset 0 0 0 1px #713ae8;
}
.ob-variant-text {
  font-size: 14px;
  font-weight: 600;
}
.ob-variant-this {
  font-size: 10px;
  font-weight: 700;
  color: #713ae8;
}
.ob-variant-count {
  grid-column: 1 / -1;
  font-size: 11.5px;
  color: var(--gray);
}
.ob-vprod {
  border: 1px solid var(--divider);
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.ob-vprod-head {
  padding: 9px 12px;
  border-bottom: 1px solid var(--divider);
  background: var(--extra-light);
}
.ob-vprod-body {
  padding: 4px 12px 10px;
  overflow-y: auto;
  max-height: 52vh;
}
.ob-dlg-foot--note {
  display: block;
  font-size: 11.5px;
  color: var(--gray);
  line-height: 1.45;
}
.ob-dlg {
  width: 100%;
  max-width: 460px;
  border-radius: 14px;
  background: var(--white-color);
  overflow: hidden;
}
.ob-dlg-head {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--divider);
}
.ob-dlg-head h5 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
}
.ob-dlg-head .btn-close {
  margin-left: auto;
}
.ob-dlg-body {
  padding: 14px 16px;
  max-height: 60vh;
  overflow-y: auto;
}
.ob-dlg-foot {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 16px;
  border-top: 1px solid var(--divider);
  background: var(--extra-light);
}

/* mezők */
.obf {
  margin: -1px 0 8px;
  padding: 11px 12px 12px;
  border: 1px solid #713ae8;
  border-top: 0;
  border-radius: 0 0 9px 9px;
  background: #f7fafd;
}
.obf-field {
  margin-bottom: 9px;
}
.obf-label {
  display: block;
  margin-bottom: 3px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--gray-light);
}
.obf-row {
  display: grid;
  grid-template-columns: 140px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
}
.obf-time {
  display: flex;
  align-items: center;
  gap: 7px;
}
.obf-time input {
  text-align: right;
}
.obf-time span {
  font-size: 12px;
  font-weight: 600;
  color: var(--gray);
}
.obf .form-check-label {
  font-size: 12px;
  color: var(--gray);
}
.obf-foot {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--divider);
}

@media (max-width: 1500px) {
  .ob-cols {
    grid-template-columns: minmax(0, 1.2fr) 340px minmax(0, 1fr);
  }
  .ob-col:nth-child(4) {
    grid-column: 1 / -1;
  }
  .ob-col:nth-child(4) .ob-side {
    height: 380px;
  }
}
@media (max-width: 1000px) {
  .ob-cols {
    grid-template-columns: 1fr;
  }
}
</style>
